"use client";

import type { ReactNode } from "react";
import { useEffect, useRef } from "react";
import { Box as MantineBox } from "@mantine/core";

import styles from "../styles.module.scss";

const REVEAL_TRANSFORMS: Record<string, string> = {
  left: "translateX(-48px)",
  right: "translateX(48px)",
  up: "translateY(36px)",
};

export function ReportPageShell({ children }: { children: ReactNode }) {
  const pageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const page = pageRef.current;
    if (!page || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const elements = page.querySelectorAll<HTMLElement>("[data-reveal]");

    elements.forEach((element) => {
      const direction = element.dataset.reveal ?? "up";
      element.style.opacity = "0";
      element.style.transform = REVEAL_TRANSFORMS[direction] ?? REVEAL_TRANSFORMS.up;
      element.style.transition = "opacity 0.65s ease, transform 0.65s ease";
      element.style.willChange = "opacity, transform";
    });

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const element = entry.target as HTMLElement;
          element.style.opacity = "1";
          element.style.transform = "none";
          element.style.willChange = "auto";
          observer.unobserve(element);
        });
      },
      { threshold: 0.08, rootMargin: "0px 0px -48px 0px" }
    );

    elements.forEach((element) => observer.observe(element));
    return () => observer.disconnect();
  }, []);

  return (
    <MantineBox ref={pageRef} className={styles.page}>
      {children}
    </MantineBox>
  );
}
